import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Footer from "../components/Footer";
import SplitPanel from "../components/SplitPanel";
import { CAMERA_NEUTRAL_GRADIENT } from "../utils/lightingGradients";
import { setPageMeta, breadcrumbLd } from "../utils/meta";



export default function About() {
  useEffect(() => {
    setPageMeta({
      title: "About: How Filtspot Generates Settings",
      description: "How this site calculates camera settings: a deterministic rule engine built from confirmed camera specs, exposure math, and lighting data, not an AI guessing.",
      path: "/about/",
      jsonLdId: "about-jsonld",
      jsonLd: {
        "@context": "https://schema.org",
        "@graph": [
          { "@type": "AboutPage", name: "About Filtspot" },
          breadcrumbLd([{ name: "Home", path: "/" }, { name: "About", path: "/about/" }]),
        ],
      },
    });
  }, []);

  return (
    <>
    <SplitPanel gradient={CAMERA_NEUTRAL_GRADIENT} imageText={{ eyebrow: "Filtspot", title: "How it works", sub: "The method, in full" }}>
    <main className="static-page split-article">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/">Home</Link> <span aria-hidden="true">/</span> <span>About</span>
      </nav>

      <h1>How this site generates recommendations</h1>

      <p>
        Every setting on this site (frame rate, shutter, ISO, white balance, ND filter and picture
        profile) comes from a deterministic rule engine, not a language model guessing at plausible
        numbers. The same camera in the same scenario and the same light will always give you the
        same recommendation, and you can trace exactly why.
      </p>

      <h2>The method</h2>
      <ol>
        <li>
          <strong>Camera capabilities are data, not assumptions.</strong> Each camera's confirmed
          specs (supported frame rates, ISO range, ND filter support, picture profiles) live in a
          structured database. Where a spec isn't confirmed, it's marked unknown rather than guessed,
          and the recommendation's confidence score reflects that.
        </li>
        <li>
          <strong>Shutter speed follows the 180° shutter rule</strong>, a standard cinematography
          convention. It's calculated straight from the chosen frame rate rather than looked up in a
          table.
        </li>
        <li>
          <strong>ISO, white balance and ND filter recommendations</strong> come from the lighting
          condition's exposure profile, kept within what the specific camera actually supports.
        </li>
        <li>
          <strong>A confidence score is attached to every recipe.</strong> It drops whenever a
          calculation leans on an unverified spec, so a lower-confidence recommendation reads as less
          certain instead of being dressed up with false precision.
        </li>
      </ol>

      <h2>What's free and what isn't</h2>
      <p>
        Every settings table, every explanation and every reference page here is free and public,
        including the ones you reach by scanning a QR code. Nothing about the recommendation itself is
        held back. If you came for the numbers, the numbers are the page.
      </p>
      <p>
        One thing is paid: on-device scene analysis, which uses your phone's camera and orientation
        sensor to compare the light and pan speed you actually have against what the recipe assumed.
        It's an addition to the answer, never a gate in front of it.
      </p>

      <h2>How this site pays for itself</h2>
      <p>
        Where the calculation says you need a filter or a faster card, there's a link to buy one and
        it earns a commission. That's the whole model. No subscription, no accounts, nothing held
        back.
      </p>
      <p>
        It works without corrupting the advice because the recommendation comes first: the filter
        shown is whatever the exposure maths landed on, and on pages where no filter is needed, no
        product appears at all. If that ever flipped, and a page started recommending something
        because it paid rather than because it was right, the site would stop being worth reading.
        That's a stronger reason to keep it honest than any policy.
      </p>

      <h2>What this site doesn't do</h2>
      <p>
        It doesn't invent camera capabilities that aren't confirmed, and it doesn't try to cover
        every camera and scenario the engine could theoretically compute. Pages are added
        deliberately, based on real demand, rather than published automatically just because a
        result exists.
      </p>

      <p>
        See our <Link to="/privacy">Privacy &amp; Cookies</Link> page for how this site handles (or
        rather, doesn't collect) data.
      </p>

    </main>
    </SplitPanel>
    <Footer />
    </>
  );
}
